import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, getYear } from "date-fns";
import { TriangleAlert } from "lucide-react";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { DatePickerField } from "components/ui/DatePickerField";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { InfoTooltip } from "components/ui/InfoTooltip";
import { Select } from "components/ui/Select/Select";
import { getReviewTypeTitle,REVIEW_TYPES } from "features/cdm/constants/reviewTypes";
import {
  applyYearPrefill,
  firstAvailableYear,
} from "features/cdm/utils/applyYearPrefill";
import { useEthnicGroups } from "features/patients/hooks/useEthnicGroups";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePatientHealthIdentifiers } from "features/patients/hooks/usePatientHealthIdentifiers";
import { resolveEthnicityCode } from "features/patients/utils";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { formatEircode } from "utils/validators";

import { diagnosisItems } from "../../constants/diagnosisItems";
import {
  getUnavailablePpReasons,
  getVisiblePpReasons,
  OCF_DERIVED_IDS,
} from "../../utils/visibleRegistrationReasons";
import style from "./AddPPReviewDialog.module.css";
import { formSchema } from "./schema";

// Ref 11a: same Pre-Diabetes criteria pop-up OCF already shows, so the GP has the
// HbA1c threshold to hand when registering a patient for Pre-Diabetes on PP.
const PRE_DIABETES_CRITERIA =
  "Pre diabetes can be diagnosed when HBA1c lies between 42 – 47 mmol / mol";

export const AddPPReviewDialog = ({
  isAddPpReviewDialogOpen,
  setIsAddPpReviewDialogOpen,
  addNewPPItem,
  doctor,
  reviewType = REVIEW_TYPES.PP_REGISTRATION,
  prefillReasonCodes = [],
  prefillReasonYears = {},
  annualPrefillReasonCodes = [],
  annualPrefillYearOfRegistration = null,
  annualPrefillAdditionalRiskFactors = [],
  annualPrefillRiskFactorYears = {},
}) => {
  const { data: staffMembers } = useStaffMembers();
  const { data: patient } = usePatientData();
  const { data: healthIdentifiers } = usePatientHealthIdentifiers();

  // OCF-referral reasons (QRISK3 / Pre-Diabetes / Previous BNP) are only offered
  // when they arrive from an OCF outcome (or carry over from a prior PP
  // registration on annual) — never as a fresh direct-entry pick. They are a
  // registration attribute, so those carried onto a PP annual stay visible.
  // Direct-entry reasons (hypertension / pregnancy) are NOT force-shown on
  // annual: they follow the normal context rules, so one that is no longer
  // valid (e.g. hypertension after losing GMS eligibility) drops off.
  const annualOcfDerivedCarryIn = (annualPrefillReasonCodes ?? []).filter((id) =>
    OCF_DERIVED_IDS.includes(id)
  );
  const reasonItems = getVisiblePpReasons({
    patient,
    haaNumber: healthIdentifiers?.haaNumber,
    alreadySelectedIds: [
      ...(prefillReasonCodes ?? []),
      ...annualOcfDerivedCarryIn,
    ],
  });
  // Reasons filtered out of the table above, with the rule each one fails, so a
  // shorter list reads as "not eligible" rather than "option missing".
  const unavailableReasons = getUnavailablePpReasons({
    patient,
    haaNumber: healthIdentifiers?.haaNumber,
    alreadySelectedIds: [
      ...(prefillReasonCodes ?? []),
      ...annualOcfDerivedCarryIn,
    ],
  });

  const { data: ethnicGroups } = useEthnicGroups();

  const [showReasonError, setShowReasonError] = useState(false);

  const missingPatientFields = [
    !patient?.ethnicityCode && "Ethnic Group",
    !patient?.address?.country && "Country",
    !patient?.financialClass && "Patient Classification",
  ].filter(Boolean);

  const currentYear = new Date().getFullYear().toString();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      consultationDate: format(new Date(), "yyyy-MM-dd"),
      eircode: "",
      contractDoctor: doctor?.staffMember?.id?.toString(),
      ethnicityCode: "",
      qrisk3: false,
      hypertenstionStage1NoTargetOrganDamage: false,
      hypertenstionStage1: false,
      hypertenstionStage2: false,
      gestationalDiabetes: false,
      preEclampsia: false,
      preDiabetes: false,
      previousBNP: false,
      qrisk3Year: currentYear,
      hypertenstionStage1NoTargetOrganDamageYear: currentYear,
      hypertenstionStage1Year: currentYear,
      hypertenstionStage2Year: currentYear,
      gestationalDiabetesYear: currentYear,
      preEclampsiaYear: currentYear,
      preDiabetesYear: currentYear,
      previousBNPYear: currentYear,
    },
  });

  useEffect(() => {
    form.setValue("contractDoctor", doctor?.staffMember?.id?.toString() ?? "");
  }, [doctor, form]);

  useEffect(() => {
    if (isAddPpReviewDialogOpen && patient?.address?.postCode) {
      form.setValue(
        "eircode",
        formatEircode(patient.address.postCode) || patient.address.postCode
      );
    }
    if (isAddPpReviewDialogOpen && patient?.ethnicityCode && ethnicGroups?.length) {
      // Normalise the patient's ethnicity (code or description) to the canonical
      // code so the Select — which matches by `g.code` — prefills correctly.
      const code = resolveEthnicityCode(patient.ethnicityCode, ethnicGroups);
      if (code) form.setValue("ethnicityCode", code);
    }
  }, [
    isAddPpReviewDialogOpen,
    patient?.address?.postCode,
    patient?.ethnicityCode,
    ethnicGroups,
    form,
  ]);

  // Prefill the reason checkboxes from a completed OCF "register on Prevention
  // Programme" outcome on a fresh PP registration (KIN-1162). Mapped by code id.
  useEffect(() => {
    if (!isAddPpReviewDialogOpen) return;
    if (reviewType !== REVIEW_TYPES.PP_REGISTRATION) return;
    if (!prefillReasonCodes?.length) return;

    diagnosisItems.forEach((item) => {
      if (prefillReasonCodes.includes(item.id)) {
        form.setValue(item.field, true);
      }
    });
    // Transferred OCF -> PP diagnosis years (empty until BE ships — KIN-1406).
    applyYearPrefill({
      form,
      items: diagnosisItems,
      selectedIds: prefillReasonCodes,
      yearById: prefillReasonYears,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddPpReviewDialogOpen, reviewType, prefillReasonCodes?.join(",")]);

  // Carry the diagnoses ticked on the patient's most recent PP registration into
  // a new PP annual review — they should be pre-checked by default. Iterating
  // over the currently-visible reasonItems (rather than all diagnosisItems)
  // applies the intersection of previously-selected and currently-visible
  // reasons: OCF-derived reasons stay (kept visible via the carry-in above),
  // while a direct-entry reason no longer valid for the patient is not ticked.
  useEffect(() => {
    if (!isAddPpReviewDialogOpen) return;
    if (reviewType !== REVIEW_TYPES.PP_ANNUAL) return;
    if (!annualPrefillReasonCodes?.length) return;

    reasonItems.forEach((item) => {
      if (annualPrefillReasonCodes.includes(item.id)) {
        form.setValue(item.field, true);
        // PP stores a single shared year of registration — seed every carried
        // reason's Year cell with it (cosmetic; the submitted value comes from
        // the form's Year of Registration field).
        if (annualPrefillYearOfRegistration) {
          form.setValue(
            `${item.field}Year`,
            annualPrefillYearOfRegistration.toString()
          );
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddPpReviewDialogOpen, reviewType, annualPrefillReasonCodes?.join(",")]);

  const handleEircodeBlur = (e) => {
    const val = e.target.value;
    if (val) {
      form.setValue("eircode", formatEircode(val), { shouldValidate: true });
    }
  };

  const handleFormSubmit = (data) => {
    const contractDoctor = staffMembers?.find(
      (item) => item.id === Number(data.contractDoctor)
    );

    const checkedItems = diagnosisItems.filter((item) => data[item.field]);

    if (checkedItems.length === 0) {
      setShowReasonError(true);

      return;
    }

    const checkedIds = checkedItems.map((item) => item.id);
    const checkedNames = checkedItems.map((item) => item.name);

    const reviewDate =
      data.consultationDate || format(new Date(), "yyyy-MM-dd");

    const isAnnual = reviewType === REVIEW_TYPES.PP_ANNUAL;

    // The per-reason year the user picked in this dialog (e.g. 2024), keyed by
    // reason id so it feeds the same firstAvailableYear helper.
    const dialogReasonYears = checkedItems.reduce(
      (acc, item) => ({ ...acc, [item.id]: data[`${item.field}Year`] }),
      {}
    );

    // Seed the form's single Year of Registration: on annual from the prior PP
    // registration; on a registration from the reason year — the OCF-transferred
    // year wins when present, otherwise the year selected in this dialog.
    // Undefined -> the form's current-year default.
    const yearOfRegistration = isAnnual
      ? annualPrefillYearOfRegistration ?? undefined
      : firstAvailableYear({
          selectedIds: checkedIds,
          yearById: { ...dialogReasonYears, ...prefillReasonYears },
        }) ?? undefined;

    // Additional risk factors (code + per-factor year) carry over on annual so
    // they seed RiskAndAssessmentForm rather than starting empty.
    const additionalRiskFactorPrefill = isAnnual
      ? {
          additionalRiskFactors: annualPrefillAdditionalRiskFactors,
          yearOfAdditionalRiskFactor: annualPrefillRiskFactorYears,
        }
      : {};

    addNewPPItem({
      id: crypto.randomUUID(),
      date: new Date(reviewDate),
      reviewDate,
      name: getReviewTypeTitle(reviewType),
      reviewType,
      status: "Incomplete",
      type: "pp",
      registeredDate: new Date(),
      reviewingDate: new Date(),
      reviewingDoctor:
        contractDoctor?.firstName + " " + contractDoctor?.lastName,
      createdBy:
        doctor?.staffMember?.firstName + " " + doctor?.staffMember?.lastName,
      reasonForReview: checkedNames.join(", "),
      indications: checkedNames,
      registrationReasons: checkedIds,
      yearOfRegistration,
      ...additionalRiskFactorPrefill,
      medications: [],
      eircode: data.eircode ? data.eircode.replace(/\s+/g, "") : data.eircode,
      contractDoctor: data.contractDoctor,
      ethnicityCode: data.ethnicityCode,
      qrisk3: data.qrisk3,
      hypertenstionStage1NoTargetOrganDamage: data.hypertenstionStage1NoTargetOrganDamage,
      hypertenstionStage1: data.hypertenstionStage1,
      hypertenstionStage2: data.hypertenstionStage2,
      gestationalDiabetes: data.gestationalDiabetes,
      preEclampsia: data.preEclampsia,
      preDiabetes: data.preDiabetes,
      previousBNP: data.previousBNP,
      qrisk3Year: data.qrisk3Year,
      hypertenstionStage1NoTargetOrganDamageYear: data.hypertenstionStage1NoTargetOrganDamageYear,
      hypertenstionStage1Year: data.hypertenstionStage1Year,
      hypertenstionStage2Year: data.hypertenstionStage2Year,
      gestationalDiabetesYear: data.gestationalDiabetesYear,
      preEclampsiaYear: data.preEclampsiaYear,
      preDiabetesYear: data.preDiabetesYear,
      previousBNPYear: data.previousBNPYear,
    });
    setIsAddPpReviewDialogOpen(false);
  };

  const importedData = {
    gmsNumber:
      healthIdentifiers?.medicalCardNo || patient?.medicalCardNumber || "",
    contractDoctor:
      doctor?.staffMember?.firstName + " " + doctor?.staffMember?.lastName,
    patientDOB: patient?.dateOfBirth,
  };

  const isAnyReasonSelected = reasonItems.some((item) =>
    form.watch(item.field)
  );

  return (
    <Dialog
      open={isAddPpReviewDialogOpen}
      title="Add Prevention Programme Review"
      onClose={() => setIsAddPpReviewDialogOpen(false)}
      width={1100}
    >
      <div style={{ overflowY: "auto", maxHeight: "calc(95vh - 140px)" }}>
        {missingPatientFields.length > 0 && (
          <div style={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "12px", padding: "16px", marginBottom: "16px", color: "#856404", fontSize: "14px", fontWeight: "600" }}>
            ⚠️ PP cannot be submitted — patient is missing {missingPatientFields.join(" and ")}. Please update the patient profile first.
          </div>
        )}
        <div className={style.importedDataWrapper}>
          <div className={style.importedDataItem}>
            <p className={style.importedDataItemLabel}>GMS Number</p>
            <p className={style.importedDataItemValue}>
              {importedData.gmsNumber}
            </p>
          </div>
          <div className={style.importedDataItem}>
            <p className={style.importedDataItemLabel}>Contract Doctor</p>
            <p className={style.importedDataItemValue}>
              {importedData.contractDoctor}
            </p>
          </div>
          <div className={style.importedDataItem}>
            <p className={style.importedDataItemLabel}>Patient DOB</p>
            <p className={style.importedDataItemValue}>
              {importedData.patientDOB}
            </p>
          </div>
        </div>
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("AddPPReviewDialog"))}
          >
            <div className={style.inputGroupGrid4Columns}>
              <FormField name="consultationDate">
                <label className={style.pregnancyFormLabel}>
                  Consultation date
                </label>
                <DatePickerField
                  value={form.watch("consultationDate")}
                  onChange={(value) =>
                    form.setValue("consultationDate", value, {
                      shouldValidate: true,
                    })
                  }
                  maxDate={new Date()}
                />
              </FormField>
              <FormField name="contractDoctor">
                <label className={style.addReferralFormLabel}>
                  Contract Doctor
                </label>
                <Select
                  options={staffMembers?.map((staff) => ({
                    value: staff.id,
                    label: `${staff.firstName} ${
                      staff.lastName
                    }${
                      staff.staffRole
                        ? ` (${staff.staffRole.toLowerCase()})`
                        : ""
                    }${staff.active ? "" : " (inactive)"}`,
                  }))}
                  value={form.watch("contractDoctor") ?? ""}
                  onChange={(value) =>
                    form.setValue("contractDoctor", value, {
                      shouldValidate: true,
                    })
                  }
                  required
                />
              </FormField>
              <FormField name="ethnicityCode">
                <label className={style.addReferralFormLabel}>Ethnicity</label>
                <Select
                  options={(ethnicGroups ?? []).map((group) => group.description)}
                  value={ethnicGroups?.find((g) => g.code === form.watch("ethnicityCode"))?.description ?? ""}
                  onChange={(value) => {
                    const selectedGroup = ethnicGroups?.find((g) => g.description === value);
                    form.setValue("ethnicityCode", selectedGroup?.code ?? "", { shouldValidate: true });
                  }}
                  required
                />
              </FormField>
              <FormField name="eircode">
                <label className={style.pregnancyFormLabel}>Eircode</label>
                <input
                  type="text"
                  className="form-control"
                  {...form.register("eircode")}
                  onBlur={handleEircodeBlur}
                />
              </FormField>
            </div>
            <div className={style.indicatorsSection}>
              <h2 style={{ fontSize: "18px", fontWeight: "bold" }}>
                Diagnosis
              </h2>
              {showReasonError && !isAnyReasonSelected && (
                <div className={style.reasonRequiredMessage} role="status">
                  <TriangleAlert
                    size={20}
                    className={style.reasonRequiredIcon}
                  />
                  <p className={style.reasonRequiredText}>
                    Select at least one reason to start the review.
                  </p>
                </div>
              )}
              <div className={style.tableContainer}>
                <table className={style.table}>
                  <thead className={style.thead}>
                    <tr>
                      <th className={style.th}></th>
                      <th className={style.th}>Reason</th>
                      <th className={style.th}>Year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reasonItems.map((item) => (
                      <tr key={item.id}>
                        <td
                          className={style.tdName}
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "40px",
                          }}
                        >
                          <CustomCheckbox
                            checked={form.watch(item.field)}
                            onChange={(e) => {
                              form.setValue(item.field, e.target.checked);
                            }}
                          />
                        </td>
                        <td
                          title="Diagnosis"
                          className={style.tdName}
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "300px",
                          }}
                        >
                          {item.name}
                          {item.id === "PRE_DIABETES" && (
                            <InfoTooltip text={PRE_DIABETES_CRITERIA} />
                          )}
                        </td>
                        <td
                          title="Year"
                          className={style.tdName}
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "140px",
                          }}
                        >
                          <Select
                            small
                            options={Array.from(
                              { length: 150 },
                              (_, i) => getYear(new Date()) - i
                            ).map((year) => ({
                              value: year,
                              label: year,
                            }))}
                            disabled={!form.watch(item.field)}
                            value={form.watch(`${item.field}Year`) ?? ""}
                            onChange={(value) =>
                              form.setValue(`${item.field}Year`, value)
                            }
                            required
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {unavailableReasons.length > 0 && (
                <div className={style.unavailableSection}>
                  <p className={style.unavailableTitle}>
                    Not available for this patient
                  </p>
                  {unavailableReasons.map(({ id, name, reason }) => (
                    <div key={id} className={style.unavailableRow}>
                      <span className={style.unavailableName}>{name}</span>
                      <span className={style.unavailableReason}>{reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setIsAddPpReviewDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={missingPatientFields.length > 0}>
                Confirm
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </div>
    </Dialog>
  );
};
